import React from 'react';
import { Box, Flex } from '@storyofams/react-ui';
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';

import { GetStaticPaths, GetStaticProps, NextPage } from 'next';

import { useRouter } from 'next/router';
import nextId from 'react-id-generator';
import SbEditable from 'storyblok-react';
import { Container, DynamicSections, Seo, Heading } from '~components';
import { enhancedStaticProps, withLayout } from '~lib';
import { TableOfContents } from '~components/common/TableOfContents';

interface GuidePageProps extends WithStoryProps {}

const GuidePage: NextPage<GuidePageProps> = ({ story }) => {
  const router = useRouter();
  let tableOfContents = [];

  const content = story?.content?.body?.map((section) => ({
    ...section,
    components: section.components?.map((c) => {
      if (c.component === 'rich_text') {
        return {
          ...c,
          text: {
            ...c.text,
            content: c.text.content?.map((block) => {
              if (block.type === 'heading' && block.attrs?.level === 3) {
                const id = `l${nextId()}`;

                tableOfContents.push({
                  link: `/guides/${router.query.slug}#${id}`,
                  label: block.content?.[0]?.text,
                });

                return {
                  ...block,
                  attrs: {
                    ...block.attrs,
                    id,
                    pt: '120px',
                    mt: '-120px',
                  },
                };
              }
              return block;
            }),
          },
        };
      }

      if (c.component === 'title') {
        tableOfContents.push({
          label: c.text,
          link: `/guides/${router.query.slug}#l${c._uid}`,
        });

        return { ...c, id: `l${c._uid}`, pt: '120px', mt: '-120px' };
      }

      return c;
    }),
  }));

  return (
    <>
      <Seo story={story} />

      <SbEditable content={story?.content}>
        <Flex>
          <Box>
            <Container>
              <Box>
                <Heading variant="h2" as="h1">
                  {story?.content?.title}
                </Heading>
              </Box>
            </Container>
            <DynamicSections sections={content} />
          </Box>

          {!!tableOfContents?.length && (
            <TableOfContents
              label="On this page"
              tableLinks={tableOfContents || []}
            />
          )}
        </Flex>
      </SbEditable>
    </>
  );
};

export default withLayout(withStory(GuidePage));

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    fallback: 'blocking',
    paths: [],
  };
};

export const getStaticProps: GetStaticProps<GuidePageProps> =
  enhancedStaticProps(async ({ sdk, params }) => {
    let story = null;
    let notFound = false;

    try {
      story = (
        await sdk.guide({
          slug: `/guides/${params.slug}`,
        })
      ).GuideItem;
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  });
